import { call, put, select, take, takeEvery } from 'redux-saga/effects';

import { selectors as userSelectors } from '@elab/core/store/user';
import {
    actions as cartActions,
    selectors as cartSelectors,
    types as cartTypes,
} from '@elab/core/store/cart';
import { removeEntriesByProductCode } from '@elab/core/store/cart/saga';
import checkoutTypes from '@elab/core/store/checkout/types';
import { actions as uiActions, types as uiTypes } from '@elab/core/store/ui';
import logger from '@elab/core/utils/logger';
import * as storage from '@elab/core/utils/storage';
import animationHelpers from '@elab/core/utils/ui/animationHelpers';
import { storageNames } from '@elab/core/utils/constants';
import { selectors as configSelectors } from '@elab/core/store/config';

import actions from './actions';
import selectors from './selectors';
import types from './types';
import api from './api';

const freeSamplesFeatureCode = 'FREE_SAMPLES';

export function* initializeSamples(action = {}) {
    yield put(actions.loadSamples(action.payload));
    yield put(actions.setCategoryCode(action.payload.categoryCode));
    yield call(updateRemoveAllCheckboxState);
}

export function* loadSamples({ payload = {} }) {
    try {
        const cartLoaded = yield select(cartSelectors.isLoaded);
        if (!cartLoaded) {
            yield take(cartTypes.SET_CART_DATA);
        }
        const products = yield call(api.categoryProducts, payload.categoryCode);
        const allowedQuantity = yield select(cartSelectors.getAllowedSamplesQuantity);
        yield put(actions.setProducts(products));
        const productsListNotEmpty = Array.isArray(products)
            ? !!products.length
            : !!Object.values(products).length;
        if (productsListNotEmpty && allowedQuantity > 0) {
            yield put(actions.setHideFreeSamples(false));
            const isAccordionClosed = yield select(
                configSelectors.isFreeSamplesAccordionDefaultClosed,
            );
            if (!isAccordionClosed) {
                yield put(uiActions.accordionOpen({ name: 'free-samples' }));
            }
        } else {
            yield put(actions.setHideFreeSamples(true));
        }
    } catch (e) {
        yield call(logger.error, e);
    }
}

export function* onRemoveSamples({ payload = {} }) {
    yield put(actions.setRemoveSamplesCheckbox(payload.value));

    const userId = yield select(userSelectors.getUserId);
    const cartId = yield select(userId ? cartSelectors.getCode : cartSelectors.getGuid);

    try {
        yield put(actions.setLoading(true));
        yield call(api.refuseFreeSamples, userId, cartId, payload.value);
        if (payload.value) {
            const categoryCode = yield select(selectors.getCategoryCode);
            const samples = yield select(cartSelectors.getEntriesByCategory, categoryCode);
            const codes = samples.map(sample => sample.product.code);
            if (codes.length) {
                yield call(removeEntriesByProductCode, codes);
            }
        }
    } catch (e) {
        yield call(logger.error, e);
    }

    yield put(cartActions.loadCart());
    yield call(updateRemoveAllCheckboxState);
    yield put(actions.setLoading(false));
}

export function* updateRemoveAllCheckboxState() {
    yield take(cartTypes.SET_CART_DATA);
    const freeSamplesRefused = yield select(cartSelectors.getFreeSamplesRefused);
    yield put(actions.setRemoveSamplesCheckbox(freeSamplesRefused));
}

export function* watchDeliveryMode() {
    yield take(cartTypes.SET_CART_DATA);
    const { supportedFeatures = [] } = yield select(cartSelectors.getDeliveryMode);
    const supportsFreeSamples = supportedFeatures.some(fc => fc.code === freeSamplesFeatureCode);
    const allowedQuantity = yield select(cartSelectors.getAllowedSamplesQuantity);
    const samples = yield select(selectors.getSamples);
    const showFreeSamples = supportsFreeSamples && samples.length && allowedQuantity > 0;
    yield put(actions.setHideFreeSamples(!showFreeSamples));
}

export function* onChangeSamplesCheckbox({ payload }) {
    if (!payload) {
        yield call(storage.remove, storageNames.freeSamplesRefused);
    }
}

export function* onFreeSamplesAccordionOpen({ payload }) {
    if (payload.name === 'free-samples') {
        yield put(actions.setHideFreeSamples(false));
        // simulating resize event to trigger free samples accordion and carousel resize
        yield call(animationHelpers.simulateResizeEvent);
    }
}

export default () => [
    takeEvery(types.LOAD_SAMPLES, loadSamples),
    takeEvery(types.INITIALIZE, initializeSamples),
    takeEvery(types.REMOVE_SAMPLES, onRemoveSamples),
    takeEvery(types.SET_REMOVE_SAMPLES_CHECKBOX, onChangeSamplesCheckbox),
    takeEvery(checkoutTypes.SET_DELIVERY_MODE, watchDeliveryMode),
    takeEvery(uiTypes.ACCORDION_OPEN, onFreeSamplesAccordionOpen),
];
